import { LOCATION_NAMES, locationName } from "../Matches/locationNames";
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CalendarDays, MapPin, Search, X } from "lucide-react";
import DiscoverySelect from "../Matches/DiscoverySelect";
import MatchLocationMap, { getMatchCoordinates } from "../Matches/MatchLocationMap";
import usePublicMatches from "../Matches/usePublicMatches";
import { DISCIPLINES, matchDateLabel, matchKey, matchLocation, registrationLabel } from "../Matches/matchDiscovery";
import { matchVenueDate } from "../Matches/matchTime";
import { groupRelatedMatches, sideMatchLabel } from "../Matches/relatedMatchDiscovery";
import "./Leaderboard.css";
import "../Matches/MatchDiscovery.css";

const REGIONS = Object.keys(LOCATION_NAMES);
const STATUS_TITLES = { scheduled: "Upcoming matches", completed: "Past matches", in_progress: "Live matches", all: "All matches" };

function MatchCard({ match, mapped, selected, onSelect, cardRef, today, navigationState, side = false }) {
  const navigate = useNavigate();
  const hintId = useId();
  const date = matchVenueDate(match);
  const registration = registrationLabel(match, today);
  const opensDetails = selected || !mapped;
  function activate() {
    if (opensDetails) navigate(match.detailRoute, { state: navigationState });
    else onSelect();
  }
  return <article ref={cardRef} className={`discovery-card${side ? " discovery-side-row" : ""}${selected ? " is-selected" : ""}`} aria-label={match.matchName || "Untitled match"}>
    <button className="discovery-card-select" type="button" onClick={activate}
      aria-pressed={mapped ? selected : undefined} aria-describedby={selected ? hintId : undefined}
      aria-label={opensDetails ? `Open details for ${match.matchName || "untitled match"}` : `Select ${match.matchName || "untitled match"} on map`}>
      {!side && <time className="discovery-date" dateTime={date ? String(match.startDate || match.matchDate) : undefined}>
        <span>{date ? date.toLocaleDateString(undefined, { month: "short" }) : "Date"}</span>
        <strong>{date ? date.getDate() : "—"}</strong>
        <span>{date ? date.toLocaleDateString(undefined, { weekday: "short" }) : "TBA"}</span>
      </time>}
      <span className="discovery-card-copy">
        {side && <span className="discovery-side-label">{sideMatchLabel(match)}</span>}
        <span className="discovery-card-title">{match.matchName || "Untitled match"}</span>
        {side ? <span className="discovery-discipline">{matchDateLabel(match)}</span> : <span className="discovery-discipline">{DISCIPLINES[match.competitionType]} <span>· {date ? date.getFullYear() : "Date to be announced"}</span></span>}
        <span className="discovery-location"><MapPin size={14} aria-hidden="true" />{matchLocation(match)}</span>
        <span className={`discovery-registration ${registration.tone}`}><span aria-hidden="true" />{registration.label}</span>
      </span>
    </button>
    <div className="discovery-card-bottom">
      <span>{!mapped ? "Map location unavailable" : side ? "" : matchDateLabel(match)}
        {selected && <span id={hintId} className="discovery-card-hint">Select again to view details</span>}
      </span>
      <Link to={match.detailRoute} state={navigationState} aria-label={`View ${match.matchName || "match"}`}>
        View match <ArrowRight size={15} aria-hidden="true" />
      </Link>
    </div>
  </article>;
}

export function PublicMatchesPage() {
  const [params, setParams] = useSearchParams();
  const route = useLocation();
  const navigationState = { discoveryPath: route.pathname + route.search };
  const status = Object.hasOwn(STATUS_TITLES, params.get("status")) ? params.get("status") : "scheduled";
  const family = Object.hasOwn(DISCIPLINES, params.get("family")) ? params.get("family") : "all";
  const query = params.get("q") || "";
  const location = params.get("location") || "";
  const date = status === "completed" ? "any" : ["week", "month"].includes(params.get("date")) ? params.get("date") : "any";
  const sort = ["soonest", "latest"].includes(params.get("sort")) ? params.get("sort") : status === "completed" ? "latest" : "soonest";
  const [searchInput, setSearchInput] = useState(query);
  const [selectedId, setSelectedId] = useState("");
  const [area, setArea] = useState(null);
  const [today, setToday] = useState(() => new Date());
  const boundsRef = useRef(null);
  const resultsRef = useRef(null);
  const cardRefs = useRef(new Map());
  const pendingMapSelection = useRef("");
  const { matches, locations: availableLocations, failures, hasMore, loading, loadingMore, retry, loadMore } = usePublicMatches({ status, family, query, location, date, sort, area }, today.toDateString());

  useEffect(() => { setSearchInput(query); }, [query]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setToday((previous) => previous.toDateString() === new Date().toDateString() ? previous : new Date());
    }, 60000);
    return () => window.clearInterval(timer);
  }, []);

  const locations = useMemo(() => [...new Set([...REGIONS, ...availableLocations])].sort((a, b) => locationName(a).localeCompare(locationName(b))), [availableLocations]);
  const selectedMatch = matches.find(match => matchKey(match) === selectedId);
  const mapMatches = matches;
  const visibleMatches = matches;
  const matchGroups = useMemo(() => groupRelatedMatches(matches), [matches]);
  const mappedIds = useMemo(() => new Set(mapMatches.filter(getMatchCoordinates).map(matchKey)), [mapMatches]);
  const activeFilters = Boolean(query || location || date !== "any" || family !== "all" || area);
  const failedAll = !loading && failures.length > 0 && matches.length === 0;
  const title = STATUS_TITLES[status];

  useEffect(() => {
    const card = cardRefs.current.get(pendingMapSelection.current);
    if (card && resultsRef.current) {
      resultsRef.current.scrollTo({ top: card.offsetTop - 12, behavior: "auto" });
      pendingMapSelection.current = "";
    }
  }, [visibleMatches]);

  useEffect(() => {
    setArea(null);
    setSelectedId("");
    if (resultsRef.current) resultsRef.current.scrollTop = 0;
  }, [status, family, query, location, date]);

  const handleBounds = useCallback((bounds) => { boundsRef.current = bounds; }, []);
  function updateFilter(name, value) {
    const next = new URLSearchParams(params);
    if (!value || value === "all" || value === "any") next.delete(name);
    else next.set(name, value);
    setParams(next, { replace: true });
  }
  function statusPath(value) {
    const next = new URLSearchParams(params);
    next.delete("date"); next.delete("sort");
    if (value === "scheduled") next.delete("status"); else next.set("status", value);
    return `/matches${next.toString() ? `?${next}` : ""}`;
  }
  function clearFilters() {
    const next = new URLSearchParams();
    if (status !== "scheduled") next.set("status", status);
    setParams(next, { replace: true });
    setArea(null);
  }
  function selectFromMap(key) {
    setSelectedId(key);
    const card = cardRefs.current.get(key);
    const list = resultsRef.current;
    if (card && list) {
      // Scroll the results pane only; scrollIntoView would also move the page/map.
      list.scrollTo({ top: card.offsetTop - 12, behavior: "auto" });
    } else if (area) {
      // A pin can be outside the last searched area after the competitor pans the map.
      pendingMapSelection.current = key;
      setArea(null);
    }
  }

  return <section className="match-discovery" aria-label="Find matches">
    <Helmet><title>{title} | Range Pocket Competition</title></Helmet>
    <a className="discovery-skip" href="#match-results">Skip to match results</a>

    <section className="discovery-map" aria-label="Match map">
      <MatchLocationMap matches={mapMatches} selectedMatchId={selectedId} getMatchId={matchKey}
        onSelectMatch={selectFromMap} onBoundsChange={handleBounds} showWhenEmpty dateMarkers scrollWheelZoom={false}
        className="discovery-map-canvas" mapLabel="Map of matching events"
        renderPopupContent={(match) => <div className="discovery-popup">
          <strong>{match.matchName || "Untitled match"}</strong>
          {match.parentMatchId && <span>{sideMatchLabel(match)}</span>}
          <span>{matchDateLabel(match)}</span>
          <span>{DISCIPLINES[match.competitionType]} · {matchLocation(match)}</span>
          <Link to={match.detailRoute} state={navigationState}>View match <ArrowRight size={14} aria-hidden="true" /></Link>
        </div>} />
      <div className="discovery-map-actions">
        <button type="button" disabled={loading} onClick={() => { if (boundsRef.current) setArea({ ...boundsRef.current }); }}>
          <Search size={16} aria-hidden="true" /> Search this area
        </button>
        {area && <button type="button" className="discovery-area-clear" onClick={() => setArea(null)} aria-label="Clear map area filter"><X size={16} /></button>}
      </div>
      <div className="discovery-map-caption" role="status">
        {loading ? "Finding matches…" : selectedMatch ? `Selected: ${selectedMatch.matchName} · ${matchDateLabel(selectedMatch)}` : mappedIds.size ? `${mappedIds.size}${hasMore ? "+" : ""} mapped · Select a pin to explore` : "No map locations for these matches"}
      </div>
    </section>

    <section className="discovery-browser" aria-label="Browse matches">
      <div className="discovery-controls">
        <header className="discovery-heading">
          <h1>{status === "scheduled" ? <><span className="discovery-desktop-heading">Find your next match</span><span className="discovery-mobile-heading">Upcoming matches</span></> : title}</h1>
          <p>{status === "scheduled" ? "Upcoming NRA ARC & Bullseye events" : status === "completed" ? "Explore previous events and match details" : "Explore NRA ARC & Bullseye events"}</p>
        </header>
        <nav className="discovery-periods" aria-label="Match timeframe"><Link to={statusPath("scheduled")} aria-current={status === "scheduled" || status === "in_progress" ? "page" : undefined}>Upcoming & live</Link><Link to={statusPath("completed")} aria-current={status === "completed" ? "page" : undefined}>Past matches</Link></nav>
        <form className="discovery-search" role="search" onSubmit={(event) => { event.preventDefault(); updateFilter("q", searchInput.trim()); }}>
          <Search size={18} aria-hidden="true" />
          <input type="search" aria-label="Match name or location" placeholder="Match name or location" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
          <button type="submit" aria-label="Search matches"><ArrowRight size={18} aria-hidden="true" /></button>
        </form>
        <div className={`discovery-filters${status === "completed" ? " is-past" : ""}`}>
          <DiscoverySelect searchable icon={MapPin} label="Location" value={location} onChange={(value) => updateFilter("location", value)}
            options={[{ value: "", label: "Anywhere" }, ...(location && !locations.includes(location) ? [location, ...locations] : locations).map((state) => ({ value: state, label: locationName(state) }))]} />
          {status !== "completed" && <DiscoverySelect icon={CalendarDays} label="Date" value={date} onChange={(value) => updateFilter("date", value)}
            options={[{ value: "any", label: "Any date" }, { value: "week", label: "Next 7 days" }, { value: "month", label: "This month" }]} />}
          <DiscoverySelect label="Discipline" value={family} onChange={(value) => updateFilter("family", value)}
            options={[{ value: "all", label: "All disciplines" }, { value: "nra_arc", label: "NRA ARC" }, { value: "bullseye", label: "Bullseye" }]} />
        </div>
        <div className="discovery-result-summary">
          <span role="status">{loading ? "Finding matches…" : `${visibleMatches.length}${hasMore ? "+" : ""} ${visibleMatches.length === 1 ? "match" : "matches"}${area ? " in this area" : ""}`}</span>
          <div>{activeFilters && <button type="button" className="discovery-clear-filters" onClick={clearFilters}>Clear filters</button>}
            <DiscoverySelect compact label="Sort matches" value={sort} onChange={(value) => updateFilter("sort", value)}
              options={[{ value: "soonest", label: "Soonest first" }, { value: "latest", label: "Latest first" }]} />
          </div>
        </div>
      </div>

      <div id="match-results" ref={resultsRef} className="discovery-results" role="region" aria-label="Match results" tabIndex={0} aria-busy={loading}>
        {loading ? <div className="discovery-skeletons" role="status" aria-label="Loading matches">
          {[0, 1, 2].map((key) => <div className="discovery-skeleton" key={key}><span /><div><span /><span /><span /></div></div>)}
        </div> : <>
          {failures.length > 0 && <div className="discovery-message discovery-warning" role="alert">
            <strong>{failedAll ? "Matches could not load" : "More matches could not load"}</strong>
            <p>{failedAll ? "Please try again in a moment." : "Your loaded matches are still listed below. Retry to continue the search."}</p>
            <button type="button" disabled={loadingMore} onClick={retry}>{loadingMore ? "Trying again…" : "Try again"}</button>
          </div>}
          {!failedAll && visibleMatches.length === 0 && <div className="discovery-message discovery-empty">
            <CalendarDays size={28} strokeWidth={1.5} aria-hidden="true" />
            <h2>{activeFilters ? "No matches found" : hasMore ? "No matching events loaded yet" : status === "scheduled" ? "Your next match is still ahead" : "No matches to show yet"}</h2>
            <p>{hasMore ? "The search has more results to check. Continue searching below." : activeFilters ? "Try a wider area, another date, or all disciplines." : status === "scheduled" ? "No upcoming events are published right now. You can explore past matches while new dates are added." : "Published events will appear here."}</p>
            {activeFilters && <button type="button" onClick={clearFilters}>Clear filters</button>}
            {status === "scheduled" && <Link to="/matches?status=completed">Browse past matches <ArrowRight size={15} aria-hidden="true" /></Link>}
          </div>}
          {matchGroups.map(group => <section className="discovery-match-group" key={group.key} aria-label={group.mainMatch?.matchName ? `${group.mainMatch.matchName} match group` : "Side match search results"}>
            {!group.mainMatch && <div className="discovery-parent-context"><span>Side match search results</span><Link to={`/matches/${group.family}/${encodeURIComponent(group.parentMatchId)}`} state={navigationState}>View main match <ArrowRight size={14} /></Link></div>}
            {[...(group.mainMatch ? [group.mainMatch] : []), ...group.sideMatches].map((match) => {
            const key = matchKey(match);
            return <MatchCard key={key} match={match} side={match !== group.mainMatch} mapped={mappedIds.has(key)} selected={selectedId === key}
              onSelect={() => setSelectedId(key)} today={today} navigationState={navigationState}
              cardRef={(node) => { if (node) cardRefs.current.set(key, node); else cardRefs.current.delete(key); }} />;
            })}
          </section>)}
          {hasMore && <button className="discovery-load-more" type="button" onClick={loadMore} disabled={loadingMore}>{loadingMore ? "Loading more…" : "Load more matches"}</button>}
          {visibleMatches.length > 0 && !hasMore && <p className="discovery-list-end">You’re all caught up.</p>}
        </>}
      </div>
    </section>
  </section>;
}

export const LeaderboardMatches_Page = { path: "/matches", element: <PublicMatchesPage /> };
